import React from "react";
import { Link } from "react-router-dom";
import Logo, { IvishWordmark } from "../components/site/Logo";
import useSEO from "../lib/useSEO";
import { BRAND } from "../data/site";
import {
  COMPANY_LINKS, COMPANY_SOCIALS, CEO_LINKS,
} from "../data/links";

/* The link-in-bio pages.
 *
 *   ivishai.com/links   <- the @ivish_ai bio
 *   ivishai.com/sahith  <- the @sahithvutukuri bio
 *
 * Standalone: no Header and no Footer. This is what one tap from a social bio
 * lands on, and every extra control is a way to leave again.
 *
 * It has to fit one phone screen with no scroll - that is the whole point of a
 * bio page - so the sub-label sits under the name rather than beside it, and
 * the page is built to stay inside 100dvh at six rows.
 *
 * Both pages are this one component with a different deck. Edit the lists in
 * data/links.js, not here.
 */

const Row = ({ item, n }) => {
  const inner = (
    <>
      <span
        className="font-mono text-[0.66rem] w-[22px] shrink-0 pt-[3px]"
        style={{ color: item.accent }}
      >
        {String(n).padStart(2, "0")}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block font-semibold text-[0.98rem] text-ink leading-tight">
          {item.label}
        </span>
        <span className="block text-[0.78rem] text-ink-faint mt-[3px]">
          {item.sub}
        </span>
      </span>
      <svg
        viewBox="0 0 24 12"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className="w-5 h-[10px] shrink-0 text-ink-faint transition-colors group-hover:text-ink"
      >
        <path d="M0 6h20M15 1l5 5-5 5" />
      </svg>
    </>
  );

  const cls =
    "lk-row group flex items-start gap-[13px] py-[13px] border-t border-rule " +
    "text-ink transition-[padding] duration-200 hover:pl-[6px]";

  return item.external ? (
    <a className={cls} href={item.href} target="_blank" rel="noopener noreferrer">
      {inner}
    </a>
  ) : (
    <Link className={cls} to={item.href}>
      {inner}
    </Link>
  );
};

const LinksPage = ({ links, socials, name, title, description }) => {
  useSEO(title, description);

  return (
    <div className="iv min-h-screen min-h-[100dvh] bg-paper text-ink flex items-center justify-center px-[22px] py-8">
      <main className="w-full max-w-[420px]">
        <header className="flex justify-center">
          <Logo />
        </header>

        {/* The CEO page says whose it is. The company page does not need to -
            the lockup above has already said so. */}
        {name && (
          <p className="mt-[14px] text-center font-mono text-[0.68rem] tracking-[0.1em] uppercase text-ink-muted">
            {name}
          </p>
        )}

        <nav
          className={name ? "mt-6" : "mt-[30px]"}
          aria-label={name ? `${name} links` : `${BRAND.name} links`}
        >
          {links.map((item, i) => (
            <Row key={item.label} item={item} n={i + 1} />
          ))}
          <div className="border-t border-rule" />
        </nav>

        {socials && (
          <div className="mt-[18px] flex justify-center gap-[22px]">
            {socials.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-[0.66rem] tracking-[0.1em] uppercase text-ink-muted hover:text-ink"
              >
                {s.label}
              </a>
            ))}
          </div>
        )}

        {/* Closes the way the marketing site's own link page closes: the
            wordmark large and faded, then the line that says who made all of
            this. Not a second set of links - the page is finished here. */}
        <footer className="mt-7 flex flex-col items-center gap-3">
          <IvishWordmark className="w-[132px] h-auto block opacity-45" />
          <p className="text-[0.7rem] text-ink-faint text-center">
            A product ecosystem created by {BRAND.legal}
          </p>
        </footer>
      </main>
    </div>
  );
};

export const CompanyLinks = () => (
  <LinksPage
    links={COMPANY_LINKS}
    socials={COMPANY_SOCIALS}
    title={`All our links - ${BRAND.name}`}
    description="IvishAI and the VerbX ecosystem: the websites, the apps and the console."
  />
);

export const CeoLinks = () => (
  <LinksPage
    links={CEO_LINKS}
    name="Sahith Vutukuri · Founder & CEO"
    title={`Sahith Vutukuri - ${BRAND.name}`}
    description="Sahith Vutukuri, Founder and CEO of IvishAI: the company, the VerbX ecosystem and Sera."
  />
);

export default CompanyLinks;
